import { motion } from "framer-motion";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Eye,
  Loader2,
  AlertCircle,
  GraduationCap,
  Calendar,
  Filter,
  UserPlus,
  Trophy,
  Users,
} from "lucide-react";

import {
  useScholarships,
  useDeleteScholarship,
  useQuickAddScholarshipItem,
  type Scholarship,
} from "@/services/scholarshipService";
import type { RootState } from "@/store";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import ImageUpload from "@/components/common/ImageUpload";

interface ScholarshipsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
}

const Scholarships: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [scholarshipToDelete, setScholarshipToDelete] =
    useState<Scholarship | null>(null);

  // Quick Add State
  const [quickAddType, setQuickAddType] = useState<"sponsor" | "beneficiary" | null>(null);
  const [quickAddScholarship, setQuickAddScholarship] = useState<Scholarship | null>(null);
  const [quickAddSessionId, setQuickAddSessionId] = useState<number>(0);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [quickAddData, setQuickAddData] = useState<any>({
    name: "",
    title: "",
    bio: "",
    picture: "",
    school: "",
    location: "",
    class: "",
    notes: "",
    is_main: false
  });

  const quickAddMutation = useQuickAddScholarshipItem();

  const queryParams: ScholarshipsQueryParams = {
    per_page: 10,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
    ...(statusFilter && { status: statusFilter }),
  };

  const {
    data: scholarshipsData,
    isLoading,
    error,
  } = useScholarships(queryParams);

  const deleteScholarshipMutation = useDeleteScholarship();

  const scholarships = scholarshipsData?.scholarships || [];
  const pagination = scholarshipsData?.pagination;

  const handleDeleteScholarship = (scholarship: Scholarship) => {
    setScholarshipToDelete(scholarship);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteScholarship = async () => {
    if (!scholarshipToDelete) return;

    try {
      await deleteScholarshipMutation.mutateAsync(scholarshipToDelete.id);
      setIsDeleteDialogOpen(false);
      setScholarshipToDelete(null);
      // Success feedback is handled by the mutation's onSuccess callback in scholarshipService
    } catch (error) {
      console.error("Failed to delete scholarship:", error);
      // Error feedback could be added here with a toast notification
      // For now, we keep the dialog open to show the error state
    }
  };

  const cancelDeleteScholarship = () => {
    setIsDeleteDialogOpen(false);
    setScholarshipToDelete(null);
  };

  const handleViewScholarship = (scholarship: Scholarship) => {
    navigate(`/${currentLanguage}/scholarships/${scholarship.id}`);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "expired":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLanguage);
  };

  const handleOpenQuickAdd = (scholarship: Scholarship, type: "sponsor" | "beneficiary") => {
    setQuickAddScholarship(scholarship);
    setQuickAddType(type);
    setIsQuickAddOpen(true);
    setQuickAddSessionId(scholarship.sessions?.find(s => s.is_active)?.id || scholarship.sessions?.[0]?.id || 0);
    setQuickAddData({
      name: "",
      title: "",
      bio: "",
      picture: "",
      school: "",
      location: "",
      class: "",
      notes: "",
      is_main: type === "sponsor" ? false : undefined
    });
  };

  const handleQuickAddSubmit = async () => {
    if (!quickAddScholarship || !quickAddType || !quickAddSessionId) return;

    try {
      await quickAddMutation.mutateAsync({
        id: quickAddScholarship.id,
        type: quickAddType,
        session_id: quickAddSessionId,
        data: quickAddData
      });
      setIsQuickAddOpen(false);
    } catch (error) {
      console.error("Quick add failed:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6"
        >
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("sidebar.scholarships")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.scholarships.subtitle")}
            </p>
          </div>
          <Link
            to={`/${currentLanguage}/admin/scholarships/create`}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t("admin.scholarships.createNew")}
          </Link>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                <Filter className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                {t("admin.scholarships.filters")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t("admin.scholarships.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">
                      {t("admin.scholarships.allStatuses")}
                    </option>
                    <option value="active">
                      {t("scholarships.status.active")}
                    </option>
                    <option value="inactive">
                      {t("scholarships.status.inactive")}
                    </option>
                    <option value="expired">
                      {t("scholarships.status.expired")}
                    </option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Scholarships Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                <GraduationCap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                {t("admin.scholarships.scholarshipList")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("common.loading")}
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-20 px-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md mx-auto"
                  >
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600 dark:text-red-400 text-lg font-semibold">
                      {t("admin.scholarships.errorLoading")}
                    </p>
                  </motion.div>
                </div>
              ) : scholarships.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mx-auto"
                  >
                    <div className="mb-6">
                      <svg
                        className="w-24 h-24 mx-auto text-gray-400 dark:text-gray-500 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 14l9-5-9-5-9 5 9 5z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                        />
                      </svg>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {t("scholarships.noScholarshipsTitle")}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg mb-8 leading-relaxed">
                      {t("admin.scholarships.noScholarships")}
                    </p>
                  </motion.div>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.sponsor")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.title")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.status")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.academicYear")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.createdAt")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.scholarships.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {scholarships.map((scholarship, index) => {
                          const translation = scholarship.translations?.[0];
                          const activeSession = scholarship.sessions?.find(s => s.is_active) || scholarship.sessions?.[0];
                          const mainSponsor = activeSession?.sponsors?.find(sp => sp.is_main) || activeSession?.sponsors?.[0];

                          return (
                            <motion.tr
                              key={scholarship.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-gray-800 dark:hover:to-gray-800 transition-all duration-200"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {mainSponsor?.name || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {translation?.title || "Untitled Scholarship"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge
                                  variant={getStatusBadgeVariant(
                                    scholarship.status
                                  )}
                                  className="font-medium"
                                >
                                  {t(
                                    `scholarships.status.${scholarship.status}`
                                  )}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  {activeSession?.academic_year || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  {formatDate(scholarship.created_at!)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-1">
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleOpenQuickAdd(scholarship, "sponsor")}
                                    className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20 transition-colors"
                                    title="Quick Add Sponsor"
                                  >
                                    <Trophy className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleOpenQuickAdd(scholarship, "beneficiary")}
                                    className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 transition-colors"
                                    title="Quick Add Student"
                                  >
                                    <UserPlus className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleViewScholarship(scholarship)
                                    }
                                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                                    title={t("admin.scholarships.view")}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </motion.button>
                                  <Link
                                    to={`/${currentLanguage}/admin/scholarships/${scholarship.id}/edit`}
                                    className="p-2 rounded-lg text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors inline-block"
                                    title={t("admin.scholarships.edit")}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Link>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleDeleteScholarship(scholarship)
                                    }
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                                    title={t("admin.scholarships.delete")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </motion.button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-4 p-6">
                    {scholarships.map((scholarship, index) => {
                      const translation = scholarship.translations?.[0];
                      const activeSession = scholarship.sessions?.find(s => s.is_active) || scholarship.sessions?.[0];
                      const mainSponsor = activeSession?.sponsors?.find(sp => sp.is_main) || activeSession?.sponsors?.[0];

                      return (
                        <motion.div
                          key={scholarship.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg hover:shadow-xl transition-shadow">
                            <CardContent className="p-6">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                    {translation?.title ||
                                      "Untitled Scholarship"}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {t("admin.scholarships.by")}{" "}
                                    {mainSponsor?.name || "N/A"}
                                  </p>
                                  <Badge
                                    variant={getStatusBadgeVariant(
                                      scholarship.status
                                    )}
                                    className="font-medium"
                                  >
                                    {t(
                                      `scholarships.status.${scholarship.status}`
                                    )}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap items-center gap-1">
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onClick={() => handleOpenQuickAdd(scholarship, "sponsor")}
                                    className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                                  >
                                    <Trophy className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onClick={() => handleOpenQuickAdd(scholarship, "beneficiary")}
                                    className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                                  >
                                    <UserPlus className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleViewScholarship(scholarship)
                                    }
                                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                                    title={t("admin.scholarships.view")}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </motion.button>
                                  <Link
                                    to={`/${currentLanguage}/admin/scholarships/${scholarship.id}/edit`}
                                    className="p-2 rounded-lg text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors inline-block"
                                    title={t("admin.scholarships.edit")}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Link>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleDeleteScholarship(scholarship)
                                    }
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                                    title={t("admin.scholarships.delete")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </motion.button>
                                </div>
                              </div>

                              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {t("admin.scholarships.table.academicYear")}:
                                  </span>
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {activeSession?.academic_year || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm pt-2">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {t("admin.scholarships.table.createdAt")}:
                                  </span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {formatDate(scholarship.created_at!)}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center items-center gap-2"
          >
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.previous")}
            </Button>

            {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
              .filter((page) => {
                const start = Math.max(1, currentPage - 2);
                const end = Math.min(pagination.last_page, currentPage + 2);
                return page >= start && page <= end;
              })
              .map((page) => (
                <Button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  variant={page === currentPage ? "default" : "outline"}
                  className={
                    page === currentPage
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
                      : ""
                  }
                >
                  {page}
                </Button>
              ))}

            <Button
              onClick={() =>
                setCurrentPage(Math.min(pagination.last_page, currentPage + 1))
              }
              disabled={currentPage === pagination.last_page}
              variant="outline"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.next")}
            </Button>
          </motion.div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.scholarships.confirmDelete")}</DialogTitle>
              <DialogDescription>
                {t("admin.scholarships.confirmDeleteDescription", {
                  title:
                    scholarshipToDelete?.translations?.[0]?.title ||
                    "this scholarship",
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={cancelDeleteScholarship}
                disabled={deleteScholarshipMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteScholarship}
                disabled={deleteScholarshipMutation.isPending}
              >
                {deleteScholarshipMutation.isPending
                  ? t("common.deleting")
                  : t("admin.scholarships.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quick Add Modal */}
        <Dialog open={isQuickAddOpen} onOpenChange={setIsQuickAddOpen}>
          <DialogContent className="max-w-md bg-white dark:bg-gray-800 border-0 shadow-2xl p-0 overflow-hidden rounded-2xl text-gray-900 dark:text-gray-100">
            <DialogHeader className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                {quickAddType === "sponsor" ? <Trophy className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                {quickAddType === "sponsor" ? "Quick Add Sponsor" : "Quick Add Student"}
              </DialogTitle>
              <DialogDescription className="text-blue-100">
                {quickAddScholarship?.translations?.[0]?.title}
              </DialogDescription>
            </DialogHeader>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Academic Year</Label>
                <select
                  value={quickAddSessionId}
                  onChange={(e) => setQuickAddSessionId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500"
                >
                  {quickAddScholarship?.sessions?.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.academic_year} {session.is_active ? "(Active)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Name *</Label>
                <Input
                  value={quickAddData.name}
                  onChange={(e) => setQuickAddData({ ...quickAddData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>

              {quickAddType === "sponsor" ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Title</Label>
                    <Input
                      value={quickAddData.title}
                      onChange={(e) => setQuickAddData({ ...quickAddData, title: e.target.value })}
                      placeholder="e.g. CEO, Philanthropist"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      checked={quickAddData.is_main}
                      onChange={(e) => setQuickAddData({ ...quickAddData, is_main: e.target.checked })}
                      id="is-main-sponsor"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="is-main-sponsor" className="text-sm">Main Sponsor for this year</Label>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">School</Label>
                      <Input
                        value={quickAddData.school}
                        onChange={(e) => setQuickAddData({ ...quickAddData, school: e.target.value })}
                        placeholder="School"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Class</Label>
                      <Input
                        value={quickAddData.class}
                        onChange={(e) => setQuickAddData({ ...quickAddData, class: e.target.value })}
                        placeholder="e.g. JSS 3"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Picture</Label>
                <ImageUpload
                  onUpload={async (files) => {
                    const file = files[0];
                    if (!file) return [];
                    return new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const res = e.target?.result as string;
                        setQuickAddData({ ...quickAddData, picture: res });
                        resolve([res]);
                      };
                      reader.readAsDataURL(file);
                    });
                  }}
                  onChange={(val) => {
                    const url = Array.isArray(val) ? val[0] || "" : val || "";
                    setQuickAddData({ ...quickAddData, picture: url });
                  }}
                  value={quickAddData.picture || ""}
                  multiple={false}
                  maxFiles={1}
                />
              </div>
            </div>

            <DialogFooter className="p-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800">
              <Button variant="ghost" onClick={() => setIsQuickAddOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30"
                onClick={handleQuickAddSubmit}
                disabled={quickAddMutation.isPending || !quickAddData.name}
              >
                {quickAddMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Save Entry"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

Scholarships.displayName = "Scholarships";

export default Scholarships;
